/**
 * Dense table cell for Cost Control — keeps PO/PR rows single-height.
 * Full WBS/CBS/category editor opens in a popover.
 */
import React, { useMemo } from 'react';
import { Button, Popover, Space, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  CostControlLineFields,
  type CostControlLineFieldsValue,
} from './CostControlLineFields';
import { BudgetCodeLabel } from './BudgetCodeLabel';
import { BudgetAvailabilityHint } from './BudgetAvailabilityHint';
import { projectService } from '@/services/project.service';

const { Text } = Typography;

export type CostControlLineCellProps = {
  projectId?: string | null;
  value: CostControlLineFieldsValue;
  onChange: (patch: CostControlLineFieldsValue) => void;
  disabled?: boolean;
  /** Line amount — marks Over when spend exceeds Available */
  requestedAmount?: number | null;
};

export const CostControlLineCell: React.FC<CostControlLineCellProps> = ({
  projectId,
  value,
  onChange,
  disabled,
  requestedAmount,
}) => {
  const baselineQ = useQuery({
    queryKey: ['project-budget-baseline', projectId],
    queryFn: () => projectService.getBudgetBaseline(String(projectId)),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const controlActive = !!baselineQ.data?.revision && (baselineQ.data?.lines || []).length > 0;

  const summary = useMemo(() => {
    if (!projectId) return null;
    if (!value.wbs_node_id) {
      return (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {controlActive ? 'Assign budget WBS' : 'Optional WBS'}
        </Text>
      );
    }
    return (
      <Space direction="vertical" size={0} style={{ maxWidth: 180 }}>
        <BudgetCodeLabel projectId={projectId} nodeId={value.wbs_node_id} kind="wbs" />
        <Text type="secondary" style={{ fontSize: 11 }}>
          {value.cost_category || 'Material'}
          {value.cbs_code_id ? ' · CBS set' : ''}
        </Text>
      </Space>
    );
  }, [projectId, value.wbs_node_id, value.cbs_code_id, value.cost_category, controlActive]);

  if (!projectId) {
    return (
      <Text type="secondary" style={{ fontSize: 12 }}>
        —
      </Text>
    );
  }

  const panel = (
    <div style={{ width: 320 }}>
      <CostControlLineFields
        projectId={projectId}
        value={value}
        onChange={onChange}
        disabled={disabled}
        size="middle"
        requestedAmount={requestedAmount}
      />
    </div>
  );

  return (
    <Popover
      trigger="click"
      placement="leftTop"
      destroyTooltipOnHide
      content={panel}
      title={
        <Space size={8}>
          <span>Cost control</span>
          {controlActive ? <Tag color="blue">Budget</Tag> : <Tag>Optional</Tag>}
        </Space>
      }
    >
      <Button
        type="link"
        size="small"
        disabled={disabled}
        style={{
          padding: 0,
          height: 'auto',
          textAlign: 'left',
          whiteSpace: 'normal',
          maxWidth: 200,
        }}
      >
        <Space direction="vertical" size={2} align="start">
          {summary}
          {value.wbs_node_id ? (
            <BudgetAvailabilityHint
              projectId={projectId}
              wbsNodeId={value.wbs_node_id}
              cbsCodeId={value.cbs_code_id}
              costCategory={value.cost_category || 'Material'}
              requestedAmount={requestedAmount}
              compact
            />
          ) : controlActive ? (
            <Tag color="warning" style={{ margin: 0, fontSize: 11 }}>
              WBS required
            </Tag>
          ) : null}
        </Space>
      </Button>
    </Popover>
  );
};

export default CostControlLineCell;
